import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import Select, { components as RSComponents } from "react-select"
import Chart from "react-apexcharts"
import { FaFilter, FaUser, FaBoxOpen } from "react-icons/fa"
import { customStyles } from "utils/SelectStyles"
import { DatePicker } from "antd"
import { months } from "utils/defaultpositions"
import secureLocalStorage from "react-secure-storage"
import dayjs from "dayjs"
import { FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"

const { RangePicker } = DatePicker

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
})).reverse()

const fmtGBP = v => {
  const abs = Math.abs(v)
  return (
    (v < 0 ? "-£" : "£") +
    abs.toLocaleString("en-GB", { maximumFractionDigits: 2 })
  )
}

const SELECT_ALL_VALUE = "__select_all__"

const CheckboxOption = ({
  innerProps,
  label,
  isSelected,
  isFocused,
  data,
  selectProps,
}) => {
  const isSelectAll = data?.value === SELECT_ALL_VALUE

  const allOpts = isSelectAll
    ? (selectProps?.options || []).filter(o => o.value !== SELECT_ALL_VALUE)
    : []
  const selectedCount = isSelectAll ? (selectProps?.value || []).length : 0
  const allSelected =
    isSelectAll && allOpts.length > 0 && selectedCount === allOpts.length

  const checked = isSelectAll ? allSelected : isSelected

  return (
    <div
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 10px",
        cursor: "pointer",
        background: isFocused ? "#f1f5f9" : isSelectAll ? "#f8faff" : "#fff",
        borderBottom: isSelectAll ? "1px solid #e2e8f0" : "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => {}}
        style={{
          accentColor: "#3b82f6",
          width: 14,
          height: 14,
          flexShrink: 0,
          cursor: "pointer",
        }}
      />
      <span
        style={{
          fontSize: "13px",
          color: isSelectAll ? "#1d4ed8" : "#1e293b",
          fontWeight: checked || isSelectAll ? 700 : 400,
        }}
      >
        {label}
      </span>
    </div>
  )
}

const CollapsibleValueContainer = ({ children, ...props }) => {
  const selected = props.getValue()
  const realOpts = (props.selectProps?.options || []).filter(
    o => o.value !== SELECT_ALL_VALUE
  )
  const allSelected = realOpts.length > 0 && selected.length === realOpts.length
  if (allSelected) {
    const input = React.Children.toArray(children).slice(-1)
    return (
      <RSComponents.ValueContainer {...props}>
        <span
          style={{
            color: "#1d4ed8",
            fontWeight: 600,
            fontSize: "13px",
            padding: "2px 0",
          }}
        >
          All ({realOpts.length}) selected
        </span>
        {input}
      </RSComponents.ValueContainer>
    )
  }
  return (
    <RSComponents.ValueContainer {...props}>
      {children}
    </RSComponents.ValueContainer>
  )
}

const NetMarginPage = () => {
  const [loading, setLoading] = useState(false)
  const [netMarginData, setNetMarginData] = useState({})
  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [customerSort, setCustomerSort] = useState("asc")
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [productSort, setProductSort] = useState("asc")
  const [productMenuOpen, setProductMenuOpen] = useState(false)
  const [dateRange, setDateRange] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedYears, setSelectedYears] = useState([])
  const [currentWeek, setCurrentWeek] = useState([])
  const [maxDate, setMaxDate] = useState(null)

  useEffect(() => {
    const Months = secureLocalStorage?.getItem("Months")
    const DateRange = secureLocalStorage?.getItem("DateRange")
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")
    const Years = secureLocalStorage?.getItem("Years")

    setSelectedMonths(Months || [])
    setSelectedYears(Years || [])

    const activeDateRaw = DateRange ?? FinalRangeRaw
    const activeDateRange = activeDateRaw
      ? activeDateRaw.map(d => dayjs(d))
      : []
    setDateRange(activeDateRange)
    setCurrentWeek(activeDateRange)
    setMaxDate(FinalRangeRaw?.[1] ? dayjs(FinalRangeRaw[1]) : null)

    const fetchInitialData = async () => {
      const payload = {
        startDate: activeDateRange?.[0]?.isValid()
          ? activeDateRange[0].format("YYYY-MM-DD")
          : undefined,
        endDate: activeDateRange?.[1]?.isValid()
          ? activeDateRange[1].format("YYYY-MM-DD")
          : undefined,
        month: Months?.map(i => i?.value)?.join(",") || undefined,
        year: Years?.map(i => i?.value)?.join(",") || undefined,
      }
      const res = await FetchPostData(
        setLoading,
        "sheet/customer/report",
        payload
      )
      if (res?.data?.success) setNetMarginData(res.data?.data)
      else setNetMarginData({})
    }

    fetchInitialData()
  }, [])

  const handleFilter = async () => {
    setSelectedCustomers([])
    setSelectedProducts([])
    const res = await FetchPostData(setLoading, "sheet/customer/report", {
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      month: selectedMonths?.map(i => i?.value)?.join(","),
      year: selectedYears?.map(i => i?.value)?.join(","),
    })
    if (res?.data?.success) setNetMarginData(res.data?.data)
    else setNetMarginData({})
  }

  // ── Derived from API data ───────────────────────────────────────────────────

  const rawRecords = useMemo(() => {
    if (!netMarginData || typeof netMarginData !== "object") return []
    return Array.isArray(netMarginData)
      ? netMarginData
      : Object.values(netMarginData)
  }, [netMarginData])

  const customerOptions = useMemo(() => {
    const seen = new Set()
    const opts = []
    rawRecords.forEach(r => {
      if (!seen.has(r.customerCode)) {
        seen.add(r.customerCode)
        opts.push({ label: r.customerName, value: r.customerCode })
      }
    })
    return opts
  }, [rawRecords])

  const sortedCustomerOptions = useMemo(
    () =>
      [...customerOptions].sort((a, b) =>
        customerSort === "asc"
          ? a.label.localeCompare(b.label)
          : b.label.localeCompare(a.label)
      ),
    [customerOptions, customerSort]
  )

  const customerOptionsWithAll = useMemo(
    () =>
      sortedCustomerOptions.length > 0
        ? [
            { label: "Select All", value: SELECT_ALL_VALUE },
            ...sortedCustomerOptions,
          ]
        : sortedCustomerOptions,
    [sortedCustomerOptions]
  )

  const handleCustomerChange = selected => {
    if (!selected) return setSelectedCustomers([])
    if (selected.some(o => o.value === SELECT_ALL_VALUE)) {
      const allPicked =
        selectedCustomers.length === sortedCustomerOptions.length
      setSelectedCustomers(allPicked ? [] : sortedCustomerOptions)
      setCustomerMenuOpen(false)
    } else {
      setSelectedCustomers(selected)
    }
  }

  const productOptions = useMemo(() => {
    const seen = new Set()
    const opts = []
    rawRecords.forEach(r => {
      if (!seen.has(r.productCode)) {
        seen.add(r.productCode)
        opts.push({ label: r.productName, value: r.productCode })
      }
    })
    return opts
  }, [rawRecords])

  const sortedProductOptions = useMemo(
    () =>
      [...productOptions].sort((a, b) =>
        productSort === "asc"
          ? a.label.localeCompare(b.label)
          : b.label.localeCompare(a.label)
      ),
    [productOptions, productSort]
  )

  const productOptionsWithAll = useMemo(
    () =>
      sortedProductOptions.length > 0
        ? [
            { label: "Select All", value: SELECT_ALL_VALUE },
            ...sortedProductOptions,
          ]
        : sortedProductOptions,
    [sortedProductOptions]
  )

  const handleProductChange = selected => {
    if (!selected) return setSelectedProducts([])
    if (selected.some(o => o.value === SELECT_ALL_VALUE)) {
      const allPicked = selectedProducts.length === sortedProductOptions.length
      setSelectedProducts(allPicked ? [] : sortedProductOptions)
      setProductMenuOpen(false)
    } else {
      setSelectedProducts(selected)
    }
  }

  // customerCode → [{ name, code, value }]
  const customerProductMap = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      const cc = r.customerCode
      if (!map[cc]) map[cc] = {}
      if (!map[cc][r.productCode])
        map[cc][r.productCode] = {
          name: r.productName,
          code: r.productCode,
          value: 0,
        }
      map[cc][r.productCode].value += Number(r.netMarginContribution)
    })
    const result = {}
    Object.entries(map).forEach(([cc, prods]) => {
      result[cc] = Object.values(prods)
    })
    return result
  }, [rawRecords])

  // customerCode → total net margin contribution
  const customerTotals = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      map[r.customerCode] =
        (map[r.customerCode] || 0) + Number(r.netMarginContribution)
    })
    return map
  }, [rawRecords])

  // customerCode → customerName
  const customerNames = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      map[r.customerCode] = r.customerName
    })
    return map
  }, [rawRecords])

  // productCode → [customerCode]
  const productCustomerMap = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      if (!map[r.productCode]) map[r.productCode] = new Set()
      map[r.productCode].add(r.customerCode)
    })
    const result = {}
    Object.entries(map).forEach(([pc, set]) => {
      result[pc] = Array.from(set)
    })
    return result
  }, [rawRecords])

  // All products aggregated (for no-filter state)
  const allProductsAgg = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      if (!map[r.productCode])
        map[r.productCode] = { name: r.productName, value: 0 }
      map[r.productCode].value += Number(r.netMarginContribution)
    })
    return Object.values(map).sort((a, b) => b.value - a.value)
  }, [rawRecords])

  // ── Filter logic ───────────────────────────────────────────────────────────

  const customerScope = useMemo(() => {
    const hasC = selectedCustomers.length > 0
    const hasP = selectedProducts.length > 0

    if (!hasC && !hasP) return Object.keys(customerProductMap)

    if (hasC && hasP) {
      const selectedProductCodes = new Set(selectedProducts.map(p => p.value))
      return selectedCustomers
        .map(c => c.value)
        .filter(cc =>
          (customerProductMap[cc] || []).some(item =>
            selectedProductCodes.has(item.code)
          )
        )
    }

    if (hasC) return selectedCustomers.map(c => c.value)

    const set = new Set()
    selectedProducts.forEach(p =>
      (productCustomerMap[p.value] || []).forEach(cc => set.add(cc))
    )
    return Array.from(set)
  }, [
    selectedCustomers,
    selectedProducts,
    customerProductMap,
    productCustomerMap,
  ])

  const products = useMemo(() => {
    const hasC = selectedCustomers.length > 0
    const hasP = selectedProducts.length > 0

    if (!hasC && !hasP) return allProductsAgg

    const selectedProductCodes = hasP
      ? new Set(selectedProducts.map(p => p.value))
      : null

    const map = {}
    customerScope.forEach(cc => {
      ;(customerProductMap[cc] || []).forEach(item => {
        if (selectedProductCodes && !selectedProductCodes.has(item.code)) return
        if (!map[item.code]) map[item.code] = { name: item.name, value: 0 }
        map[item.code].value += item.value
      })
    })
    return Object.values(map).sort((a, b) => b.value - a.value)
  }, [
    customerScope,
    selectedCustomers,
    selectedProducts,
    allProductsAgg,
    customerProductMap,
  ])

  const chipCustomers = useMemo(() => {
    const hasC = selectedCustomers.length > 0
    const hasP = selectedProducts.length > 0
    if (!hasC && !hasP) return []

    const selectedProductCodes = hasP
      ? new Set(selectedProducts.map(p => p.value))
      : null

    return customerScope.map(cc => {
      const val = selectedProductCodes
        ? (customerProductMap[cc] || [])
            .filter(item => selectedProductCodes.has(item.code))
            .reduce((s, item) => s + item.value, 0)
        : customerTotals[cc] || 0
      return { label: customerNames[cc] || cc, value: val }
    })
  }, [
    customerScope,
    selectedCustomers,
    selectedProducts,
    customerProductMap,
    customerTotals,
    customerNames,
  ])

  console.log(chipCustomers)

  // ── Summary stats ──────────────────────────────────────────────────────────

  const positiveSum = products
    .filter(i => i.value > 0)
    .reduce((s, i) => s + i.value, 0)
  const negativeSum = products
    .filter(i => i.value < 0)
    .reduce((s, i) => s + i.value, 0)
  const net = positiveSum + negativeSum
  const topProduct = products[0] || null

  // ── Bar chart ──────────────────────────────────────────────────────────────

  const barProducts = useMemo(() => {
    const top5 = products.filter(i => i.value > 0).slice(0, 5)
    const bottom5 = products.filter(i => i.value < 0).slice(0, 5)
    return [...top5, ...bottom5]
  }, [products])

  const barOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        background: "transparent",
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "65%",
          distributed: true,
        },
      },
      colors: barProducts.map(i => (i.value >= 0 ? "#22C55E" : "#EF5350")),
      dataLabels: {
        enabled: true,
        textAnchor: "end",
        formatter: val => fmtGBP(val),
        style: { fontSize: "11px", fontWeight: 700, colors: ["#1a1a2e"] },
        offsetX: 30,
        background: {
          enabled: true,
          foreColor: "#fff",
          padding: 4,
          borderRadius: 4,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false },
        },
      },
      xaxis: {
        categories: barProducts.map(i => i.name),
        labels: {
          formatter: val => fmtGBP(Number(val)),
          style: { fontSize: "11px", colors: "#64748b" },
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: "11px", fontWeight: 600, colors: "#1e293b" },
        },
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            strokeDashArray: 4,
            borderColor: "#333",
            borderWidth: 2,
            label: {
              borderColor: "#333",
              style: {
                color: "#333",
                background: "#fff",
                fontSize: "10px",
                fontWeight: 700,
              },
              text: "0",
              position: "top",
            },
          },
        ],
      },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      tooltip: { y: { formatter: val => fmtGBP(val) } },
      legend: { show: false },
    }),
    [barProducts]
  )

  const barSeries = useMemo(
    () => [
      { name: "Net Margin", data: barProducts.map(i => Math.round(i.value)) },
    ],
    [barProducts]
  )

  // ── Donut chart ────────────────────────────────────────────────────────────

  const donutItems = products.filter(i => i.value !== 0)

  const DONUT_COLORS = [
    "#4361ee",
    "#22C55E",
    "#F97316",
    "#8B5CF6",
    "#EC4899",
    "#14B8A6",
    "#EAB308",
    "#EF5350",
    "#0EA5E9",
    "#A855F7",
    "#64748b",
    "#10B981",
    "#F43F5E",
    "#6366F1",
    "#D97706",
  ]

  const donutOptions = useMemo(
    () => ({
      chart: { type: "donut", toolbar: { show: false } },
      labels: donutItems.map(i => i.name),
      colors: DONUT_COLORS,
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Net Total",
                formatter: () => fmtGBP(net),
                fontSize: "13px",
                fontWeight: 700,
                color: net >= 0 ? "#15803d" : "#be123c",
              },
            },
          },
        },
      },
      legend: { show: false },
      tooltip: { y: { formatter: val => fmtGBP(val) } },
    }),
    [donutItems, net]
  )

  const donutSeries = useMemo(
    () => donutItems.map(i => Math.abs(i.value)),
    [donutItems]
  )

  const barHeight = Math.max(280, barProducts.length * 40)

  return (
    <div className="page-content">
      {loading && <Loader />}
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
          borderRadius: "16px",
          border: "1px solid #ede9fe",
          boxShadow: "0 4px 24px rgba(108,99,255,0.07)",
          padding: "16px 20px",
          marginBottom: "20px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div className="d-flex gap-3">
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#000",
                }}
              >
                Customer Net Margin Report
              </h4>
              <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                Product-level net margin contribution by customer
              </p>
            </div>
            <div
              style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
            />
            {currentWeek?.[0] && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  padding: "6px 12px",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#16a34a",
                    display: "inline-block",
                    boxShadow: "0 0 0 2px #bbf7d0",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#15803d",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Latest week Upload
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#166534",
                      fontWeight: 600,
                      marginTop: "1px",
                    }}
                  >
                    {currentWeek[0].format("DD MMM")} —{" "}
                    {currentWeek[1].format("DD MMM YYYY")}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <RangePicker
              value={dateRange}
              placeholder={["Start (Sat)", "End (Fri)"]}
              onChange={vals => setDateRange(vals || [])}
              format="DD-MM-YYYY"
              style={{ borderRadius: "8px", height: "38px" }}
              disabledDate={current =>
                maxDate ? current && current > maxDate.endOf("day") : false
              }
            />
            <Select
              placeholder="Month"
              options={months}
              styles={customStyles}
              isMulti
              value={selectedMonths}
              onChange={v => setSelectedMonths(v || [])}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Select
              placeholder="Year"
              options={years}
              styles={customStyles}
              isMulti
              value={selectedYears}
              onChange={v => setSelectedYears(v || [])}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Button
              onClick={handleFilter}
              className="btn-blue rounded-2 border-transparent"
            >
              <FaFilter size={12} />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Customer + Product Filters ─────────────────────────────────────── */}
      <Card
        className="mb-4 shadow-sm"
        style={{ borderRadius: "16px", border: "1px solid #dbeafe" }}
      >
        <CardBody style={{ background: "#f8faff", borderRadius: "16px" }}>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <FaUser size={12} color="#3b82f6" />
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#1e40af",
                  }}
                >
                  Customer(s)
                </span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  → filters products
                </span>
                <button
                  onClick={() =>
                    setCustomerSort(s => (s === "asc" ? "desc" : "asc"))
                  }
                  style={{
                    marginLeft: "auto",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: "6px",
                    padding: "2px 9px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#1d4ed8",
                    cursor: "pointer",
                    letterSpacing: "0.3px",
                  }}
                >
                  {customerSort === "asc" ? "A → Z" : "Z → A"}
                </button>
              </div>
              <Select
                isMulti
                options={customerOptionsWithAll}
                value={selectedCustomers}
                onChange={handleCustomerChange}
                styles={customStyles}
                placeholder="Select customer(s)…"
                closeMenuOnSelect={false}
                menuIsOpen={customerMenuOpen}
                onMenuOpen={() => setCustomerMenuOpen(true)}
                onMenuClose={() => setCustomerMenuOpen(false)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                components={{
                  Option: CheckboxOption,
                  ValueContainer: CollapsibleValueContainer,
                }}
                hideSelectedOptions={false}
              />
            </Col>

            <Col md={6}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <FaBoxOpen size={12} color="#8b5cf6" />
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#6d28d9",
                  }}
                >
                  Product(s)
                </span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  → filters customers
                </span>
                <button
                  onClick={() =>
                    setProductSort(s => (s === "asc" ? "desc" : "asc"))
                  }
                  style={{
                    marginLeft: "auto",
                    background: "#faf5ff",
                    border: "1px solid #e9d5ff",
                    borderRadius: "6px",
                    padding: "2px 9px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#7c3aed",
                    cursor: "pointer",
                    letterSpacing: "0.3px",
                  }}
                >
                  {productSort === "asc" ? "A → Z" : "Z → A"}
                </button>
              </div>
              <Select
                isMulti
                options={productOptionsWithAll}
                value={selectedProducts}
                onChange={handleProductChange}
                styles={customStyles}
                placeholder="Select product(s)…"
                closeMenuOnSelect={false}
                menuIsOpen={productMenuOpen}
                onMenuOpen={() => setProductMenuOpen(true)}
                onMenuClose={() => setProductMenuOpen(false)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                components={{
                  Option: CheckboxOption,
                  ValueContainer: CollapsibleValueContainer,
                }}
                hideSelectedOptions={false}
              />
            </Col>
          </Row>

          {chipCustomers.length > 0 &&
            chipCustomers.length < customerOptions.length && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "4px",
                }}
              >
                {chipCustomers.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "#fff",
                      border: `1px solid ${
                        c.value >= 0 ? "#bbf7d0" : "#fecdd3"
                      }`,
                      borderRadius: "20px",
                      padding: "4px 14px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#1e293b",
                      }}
                    >
                      {c.label}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: c.value >= 0 ? "#15803d" : "#be123c",
                      }}
                    >
                      {fmtGBP(c.value)}
                    </span>
                  </div>
                ))}
                {(() => {
                  const total = chipCustomers.reduce((s, c) => s + c.value, 0)
                  return (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        background: total >= 0 ? "#f0fdf4" : "#fff1f2",
                        border: `1px solid ${
                          total >= 0 ? "#86efac" : "#fca5a5"
                        }`,
                        borderRadius: "20px",
                        padding: "4px 14px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#64748b",
                        }}
                      >
                        Total
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 800,
                          color: total >= 0 ? "#15803d" : "#be123c",
                        }}
                      >
                        {fmtGBP(total)}
                      </span>
                    </div>
                  )
                })()}
              </div>
            )}
        </CardBody>
      </Card>

      {/* ── Stats Cards ────────────────────────────────────────────────────── */}
      <Row className="mb-4 g-3">
        {[
          {
            label: "Positive Products (Revenue)",
            value: positiveSum,
            color: "#15803d",
            bg: "#f0fdf4",
            border: "#bbf7d0",
          },
          {
            label: "Negative Products (Revenue)",
            value: negativeSum,
            color: "#be123c",
            bg: "#fff1f2",
            border: "#fecdd3",
          },
          {
            label: "Net Margin",
            value: net,
            color: net >= 0 ? "#15803d" : "#be123c",
            bg: net >= 0 ? "#f0fdf4" : "#fff1f2",
            border: net >= 0 ? "#bbf7d0" : "#fecdd3",
          },
          {
            label: "Top Product",
            value: topProduct?.value ?? 0,
            sublabel: topProduct?.name || "—",
            color: "#1e40af",
            bg: "#eff6ff",
            border: "#bfdbfe",
          },
        ].map((card, i) => (
          <Col xl={3} md={6} key={i}>
            <div
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
                borderRadius: "16px",
                padding: "16px 20px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#444",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {card.label}{" "}
                {card?.sublabel && (
                  <span className="text-black fw-bold">({card.sublabel})</span>
                )}
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: "22px",
                  fontWeight: 800,
                  color: card.color,
                }}
              >
                {fmtGBP(card.value)}
              </p>
            </div>
          </Col>
        ))}
      </Row>

      {/* ── Charts ─────────────────────────────────────────────────────────── */}
      <Row className="g-3 mb-4">
        <Col xl={6}>
          <Card className="shadow-sm h-100" style={{ borderRadius: "16px" }}>
            <CardBody>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <h5
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      color: "#1a1a2e",
                      fontSize: "15px",
                    }}
                  >
                    Net Margin by Product
                  </h5>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    Top 5 positive · Top 5 negative
                  </p>
                </div>
                <div style={{ display: "flex", gap: "12px" }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#15803d",
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: "#22C55E",
                        display: "inline-block",
                      }}
                    />
                    Positive
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#be123c",
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: "#EF5350",
                        display: "inline-block",
                      }}
                    />
                    Negative
                  </span>
                </div>
              </div>
              {products.length > 0 ? (
                <Chart
                  options={barOptions}
                  series={barSeries}
                  type="bar"
                  height={barHeight}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                    color: "#94a3b8",
                    fontSize: "14px",
                  }}
                >
                  No data
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xl={6}>
          <Card className="shadow-sm h-100" style={{ borderRadius: "16px" }}>
            <CardBody>
              <h5
                style={{
                  margin: 0,
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: "15px",
                }}
              >
                Product Contribution Share
              </h5>
              <p
                style={{
                  margin: "2px 0 12px",
                  fontSize: "12px",
                  color: "#64748b",
                }}
              >
                Distribution by absolute value
              </p>
              {donutItems.length > 0 ? (
                <>
                  <Chart
                    options={donutOptions}
                    series={donutSeries}
                    type="donut"
                    height={250}
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "6px 12px",
                      marginTop: "12px",
                      maxHeight: "180px",
                      overflowY: "auto",
                      paddingRight: "4px",
                    }}
                  >
                    {donutItems.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "3px",
                            background: DONUT_COLORS[i % DONUT_COLORS.length],
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                          title={item.name}
                        >
                          {item.name}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            color: item.value >= 0 ? "#15803d" : "#be123c",
                            flexShrink: 0,
                          }}
                        >
                          {fmtGBP(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                    color: "#94a3b8",
                    fontSize: "14px",
                  }}
                >
                  No data
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ── Top / Bottom tables ────────────────────────────────────────────── */}
      <Row className="g-3">
        <Col xl={6}>
          <Card className="shadow-sm" style={{ borderRadius: "16px" }}>
            <CardBody>
              <h5
                style={{
                  margin: "0 0 12px",
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: "15px",
                }}
              >
                Top Positive Products
              </h5>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                    {[
                      { label: "Product", align: "left" },
                      { label: "Net Margin", align: "right" },
                      { label: "Share", align: "right" },
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          textAlign: h.align,
                          padding: "6px 8px",
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products
                    .filter(i => i.value > 0)
                    .slice(0, 8)
                    .map((item, idx) => {
                      const share = positiveSum
                        ? ((item.value / positiveSum) * 100).toFixed(1)
                        : "0.0"
                      return (
                        <tr
                          key={idx}
                          style={{ borderBottom: "1px solid #f8fafc" }}
                        >
                          <td
                            style={{
                              padding: "8px",
                              fontSize: "13px",
                              color: "#1e293b",
                              fontWeight: 500,
                            }}
                          >
                            {item.name}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#15803d",
                            }}
                          >
                            {fmtGBP(item.value)}
                          </td>
                          <td style={{ padding: "8px", textAlign: "right" }}>
                            <span
                              style={{
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                borderRadius: "6px",
                                padding: "2px 8px",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#15803d",
                              }}
                            >
                              {share}%
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>

        <Col xl={6}>
          <Card className="shadow-sm" style={{ borderRadius: "16px" }}>
            <CardBody>
              <h5
                style={{
                  margin: "0 0 12px",
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: "15px",
                }}
              >
                Top Negative Products
              </h5>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                    {[
                      { label: "Product", align: "left" },
                      { label: "Net Margin", align: "right" },
                      { label: "Share of Loss", align: "right" },
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          textAlign: h.align,
                          padding: "6px 8px",
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products
                    .filter(i => i.value < 0)
                    .slice(0, 8)
                    .map((item, idx) => {
                      const share = negativeSum
                        ? ((item.value / negativeSum) * 100).toFixed(1)
                        : "0.0"
                      return (
                        <tr
                          key={idx}
                          style={{ borderBottom: "1px solid #f8fafc" }}
                        >
                          <td
                            style={{
                              padding: "8px",
                              fontSize: "13px",
                              color: "#1e293b",
                              fontWeight: 500,
                            }}
                          >
                            {item.name}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#be123c",
                            }}
                          >
                            {fmtGBP(item.value)}
                          </td>
                          <td style={{ padding: "8px", textAlign: "right" }}>
                            <span
                              style={{
                                background: "#fff1f2",
                                border: "1px solid #fecdd3",
                                borderRadius: "6px",
                                padding: "2px 8px",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#be123c",
                              }}
                            >
                              {share}%
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default NetMarginPage
